/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Body_upload_idp_metadata_v1_workspaces__slug__saml_idp_metadata_post } from '../models/Body_upload_idp_metadata_v1_workspaces__slug__saml_idp_metadata_post';
import type { IdPMetadataUrlRequest } from '../models/IdPMetadataUrlRequest';
import type { LoginResponse } from '../models/LoginResponse';
import type { Requires2FAResponse } from '../models/Requires2FAResponse';
import type { WorkspaceSAMLConfig } from '../models/WorkspaceSAMLConfig';
import type { WorkspaceSAMLConfigForPublic } from '../models/WorkspaceSAMLConfigForPublic';
import type { WorkspaceSAMLConfigUpdate } from '../models/WorkspaceSAMLConfigUpdate';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class SamlService {
  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * SP 메타데이터 다운로드
   * SP(Service Provider) 메타데이터를 다운로드합니다. IdP 설정 전에도 사용 가능합니다.
   * @param slug
   * @returns any SP 메타데이터 XML
   * @throws ApiError
   */
  public spMetadataV1WorkspacesSlugSamlMetadataGet(slug: string): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/saml/metadata',
      path: {
        slug: slug,
      },
      errors: {
        400: `메타데이터 검증 오류`,
        404: `워크스페이스를 찾을 수 없습니다`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Login
   * @param slug
   * @returns any Successful Response
   * @throws ApiError
   */
  public loginV1WorkspacesSlugSamlLoginGet(slug: string): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/saml/login',
      path: {
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Acs
   * @param slug
   * @returns any Successful Response
   * @throws ApiError
   */
  public acsV1WorkspacesSlugSamlAcsPost(
    slug: string
  ): CancelablePromise<LoginResponse | Requires2FAResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/saml/acs',
      path: {
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Initiate Logout
   * SAML 로그아웃을 시작합니다.
   *
   * IdP에 로그아웃 요청을 보내고 사용자를 IdP 로그아웃 페이지로 리디렉션합니다.
   * @param slug
   * @returns any Successful Response
   * @throws ApiError
   */
  public initiateLogoutV1WorkspacesSlugSamlLogoutGet(slug: string): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/saml/logout',
      path: {
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Sls
   * SAML Single Logout Service (SLS) 엔드포인트입니다.
   *
   * IdP로부터 로그아웃 요청을 받아 처리합니다.
   * GET과 POST 모두 지원합니다.
   * @param slug
   * @returns any Successful Response
   * @throws ApiError
   */
  public slsV1WorkspacesSlugSamlSlsPost(slug: string): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/saml/sls',
      path: {
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Sls
   * SAML Single Logout Service (SLS) 엔드포인트입니다.
   *
   * IdP로부터 로그아웃 요청을 받아 처리합니다.
   * GET과 POST 모두 지원합니다.
   * @param slug
   * @returns any Successful Response
   * @throws ApiError
   */
  public slsV1WorkspacesSlugSamlSlsGet(slug: string): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/saml/sls',
      path: {
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Config
   * @param slug
   * @returns any Successful Response
   * @throws ApiError
   */
  public getConfigV1WorkspacesSlugSamlConfigGet(
    slug: string
  ): CancelablePromise<WorkspaceSAMLConfig | null> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/saml/config',
      path: {
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Upsert Config
   * @param slug
   * @param requestBody
   * @returns WorkspaceSAMLConfig Successful Response
   * @throws ApiError
   */
  public upsertConfigV1WorkspacesSlugSamlConfigPut(
    slug: string,
    requestBody: WorkspaceSAMLConfigUpdate
  ): CancelablePromise<WorkspaceSAMLConfig> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/v1/workspaces/{slug}/saml/config',
      path: {
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * SAML 설정 삭제
   * 워크스페이스의 SAML 설정을 완전히 삭제합니다
   * @param slug
   * @returns void
   * @throws ApiError
   */
  public deleteConfigV1WorkspacesSlugSamlConfigDelete(slug: string): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/workspaces/{slug}/saml/config',
      path: {
        slug: slug,
      },
      errors: {
        404: `워크스페이스를 찾을 수 없거나 SAML 설정이 존재하지 않습니다`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Public Config
   * 워크스페이스의 SAML 설정 존재 여부와 IdP SSO URL을 공개적으로 조회합니다.
   *
   * 이 엔드포인트는 인증 없이 접근할 수 있으며,
   * SAML 설정이 존재하는지 여부와 IdP SSO URL을 제공합니다.
   * @param slug
   * @returns WorkspaceSAMLConfigForPublic Successful Response
   * @throws ApiError
   */
  public getPublicConfigV1WorkspacesSlugSamlConfigPublicGet(
    slug: string
  ): CancelablePromise<WorkspaceSAMLConfigForPublic> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/workspaces/{slug}/saml/config/public',
      path: {
        slug: slug,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * IdP 메타데이터 파일 업로드
   * IdP 메타데이터 XML 파일을 업로드하여 SAML 설정을 구성합니다
   * @param slug
   * @param formData
   * @returns WorkspaceSAMLConfig 메타데이터를 성공적으로 가져와서 SAML 설정을 업데이트했습니다
   * @throws ApiError
   */
  public uploadIdpMetadataV1WorkspacesSlugSamlIdpMetadataPost(
    slug: string,
    formData: Body_upload_idp_metadata_v1_workspaces__slug__saml_idp_metadata_post
  ): CancelablePromise<WorkspaceSAMLConfig> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/saml/idp/metadata',
      path: {
        slug: slug,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
      errors: {
        400: `잘못된 요청 - 유효하지 않은 파일, 메타데이터 형식 또는 필수 필드 누락`,
        404: `워크스페이스를 찾을 수 없습니다`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * URL에서 IdP 메타데이터 가져오기
   * URL에서 IdP 메타데이터를 가져와서 SAML 설정을 구성합니다
   * @param slug
   * @param requestBody
   * @returns WorkspaceSAMLConfig 메타데이터를 성공적으로 가져와서 SAML 설정을 업데이트했습니다
   * @throws ApiError
   */
  public importIdpMetadataFromUrlV1WorkspacesSlugSamlIdpMetadataUrlPost(
    slug: string,
    requestBody: IdPMetadataUrlRequest
  ): CancelablePromise<WorkspaceSAMLConfig> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/workspaces/{slug}/saml/idp/metadata/url',
      path: {
        slug: slug,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `잘못된 요청 - 유효하지 않은 URL, 메타데이터 형식 또는 필수 필드 누락`,
        404: `워크스페이스를 찾을 수 없습니다`,
        408: `요청 시간 초과`,
        422: `Validation Error`,
      },
    });
  }
}
